import React, { useState, useEffect } from 'react';
import styles from './EditTeamModal.module.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, TextField, Button, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { generatePassword } from '../../utils/passwordUtils';

const EditTeamModal = ({ isOpen, onClose, user, onSubmit, loading }) => {
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    admin: false,
    mustChangePassword: true,
    passwordType: 'auto',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [originalPassword, setOriginalPassword] = useState('');

  useEffect(() => {
    if (user) {
      setOriginalPassword(user.password || '');
      setForm({
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phone || '',
        admin: user.userType === 'ACCOUNT-ADMIN' || user.userType === 'SUPER-ADMIN',
        mustChangePassword: true,
        passwordType: '', // No password type selected by default
        password: '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePasswordTypeChange = (e) => {
    const value = e.target.value;
    setForm(prev => ({
      ...prev,
      passwordType: value,
      password: value === 'auto' ? generatePassword() : ''
    }));
  };

  const handleGeneratePassword = () => {
    setForm(prev => ({ ...prev, password: generatePassword() }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      targetUserId: user.id,
      workspaceId: user.workspaceId,
      adminUserId: user.adminUserId,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      phoneNumber: form.phoneNumber,
      admin: form.admin,
      mustChangePassword: form.mustChangePassword,
      password: form.passwordType === 'auto' ? form.password : form.passwordType === 'manual' ? form.password : undefined
    });
  };

  const textFieldProps = {
    variant: "outlined",
    size: "small",
    InputLabelProps: { shrink: true, style: { top: -10, fontSize: "16px", fontWeight: "600" } },
    InputProps: {
      style: {
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        background: "#fff",
        padding: "10px 12px",
        fontSize: "15px",
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(87,42,241,0.13), 0 1.5px 8px rgba(0,0,0,0.08)',
          padding: 0,
          background: '#fff',
          minWidth: 600,
          maxWidth: 800
        }
      }}
    >
      <DialogTitle>Edit User</DialogTitle>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, marginBottom: 16 }}>
        {user?.profileImage ? (
          <img src={user.profileImage} alt={user.fullName || user.name || user.email} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }} />
        ) : (
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #e2e8f0', fontSize: 28, fontWeight: 700, color: '#888' }}>
            {(user?.fullName || user?.name || user?.email || '?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit}>
        <DialogContent className={styles.dialogContent}>
          <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
            <TextField
              {...textFieldProps}
              className={styles.inputField}
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              style={{ flex: 1 }}
              margin="normal"
              required
            />
            <TextField
              {...textFieldProps}
              className={styles.inputField}
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              style={{ flex: 1 }}
              margin="normal"
              required
            />
          </div>
          <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
            <TextField
              {...textFieldProps}
              className={styles.inputField}
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              style={{ flex: 1 }}
              margin="normal"
              required
            />
            <TextField
              {...textFieldProps}
              className={styles.inputField}
              label="Phone Number"
              name="phoneNumber"
              value={form.phoneNumber}
              onChange={handleChange}
              style={{ flex: 1 }}
              margin="normal"
            />
          </div>
          <div className={styles.switchRow}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className={styles.toggleSwitch}>
                <input
                  id="edit-admin-toggle"
                  type="checkbox"
                  checked={form.admin}
                  onChange={e => setForm(prev => ({ ...prev, admin: e.target.checked }))}
                />
                <label htmlFor="edit-admin-toggle"></label>
              </div>
              <span className={styles.switchLabel}>Admin</span>
            </div>
          </div>
          <div className={styles.passwordSection}>
            <div className={styles.passwordOptions}>
              <div className={styles.passwordOption}>
                <label style={{ fontWeight: 500, marginRight: 12 }}>Password Type:</label>
                <FormControlLabel
                  control={<input type="radio" name="passwordType" value="auto" checked={form.passwordType === 'auto'} onChange={handlePasswordTypeChange} />}
                  label="Auto-generate"
                />
                <FormControlLabel
                  control={<input type="radio" name="passwordType" value="manual" checked={form.passwordType === 'manual'} onChange={handlePasswordTypeChange} />}
                  label="Manual"
                />
              </div>
            </div>
            {form.passwordType === 'auto' && (
              <>
                <div className={styles.passwordDisplay}>
                  <TextField
                    {...textFieldProps}
                    className={styles.inputField}
                    label="Password"
                    value={form.password}
                    type={showPassword ? 'text' : 'password'}
                    style={{ flex: 2 }}
                    InputProps={{
                      ...textFieldProps.InputProps,
                      endAdornment: (
                        <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                    margin="normal"
                    disabled
                  />
                  <IconButton
                    className={styles.regenerateBtn}
                    onClick={handleGeneratePassword}
                    size="small"
                    title="Regenerate password"
                    sx={{
                      border: '1px solid #572af1',
                      color: '#572af1',
                      background: '#fff',
                      borderRadius: '6px',
                      padding: '6px',
                      transition: 'background 0.2s, color 0.2s',
                      '&:hover': {
                        background: '#f3f0ff',
                        color: '#291b3b',
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </div>
                <div style={{ marginTop: 12 }}>
                  <div className={styles.toggleSwitch}>
                    <input
                      id="edit-must-change-toggle"
                      type="checkbox"
                      checked={form.mustChangePassword}
                      onChange={e => setForm(prev => ({ ...prev, mustChangePassword: e.target.checked }))}
                    />
                    <label htmlFor="edit-must-change-toggle"></label>
                  </div>
                  <span className={styles.switchLabel}>Must Change Password</span>
                </div>
              </>
            )}
            {form.passwordType === 'manual' && (
              <>
                <div className={styles.passwordDisplay}>
                  <TextField
                    {...textFieldProps}
                    className={styles.inputField}
                    label="Password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    type={showPassword ? 'text' : 'password'}
                    style={{ flex: 2 }}
                    InputProps={{
                      ...textFieldProps.InputProps,
                      endAdornment: (
                        <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                    margin="normal"
                    required
                  />
                </div>
                <div style={{ marginTop: 12 }}>
                  <div className={styles.toggleSwitch}>
                    <input
                      id="edit-must-change-toggle"
                      type="checkbox"
                      checked={form.mustChangePassword}
                      onChange={e => setForm(prev => ({ ...prev, mustChangePassword: e.target.checked }))}
                    />
                    <label htmlFor="edit-must-change-toggle"></label>
                  </div>
                  <span className={styles.switchLabel}>Must Change Password</span>
                </div>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button type="submit" className={styles.saveBtn} disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditTeamModal; 